import {
  PlanTypesEnum,
  PriceVO,
  getSmsStartingPriceFromAddOns,
  getInboxPreviewStartingPriceFromAddOns,
  type AddOnsVO,
  type PaymentPlan,
  type BillingAccount,
  type PlanGroup,
  type Plan,
  type Suspend,
  type CountryConfig,
} from '@ctct/domain-layer';
import type { ComparisonTableFeature, ComparisonTableGroup, ComparisonTablePlan } from '@ctct/payflow-ui';

import type { ContentStrategy, InitialPlan } from './ContentStrategy';
import {
  INBOX_PREVIEW_PRICE_TOKEN,
  SMS_INCLUDED_FREE_TOKEN,
  SMS_MARKETING_GROUP_ID,
  SMS_STARTING_PRICE_TOKEN,
} from './ContentStrategy';
import socialStarterContent from './SocialStarterContentStrategy/contentSocialStarter';
import type { TranslationGetter } from '../../../types';
import { planComparisonTableEventBus } from '../PlanComparisonTableEventBus';
import type { Flags } from '../types';

type PlanType = Plan['type'];

const PLAN_NAME_TO_TYPE: Record<string, PlanType> = {
  Lite: PlanTypesEnum.Bronze,
  Standard: PlanTypesEnum.Silver,
  Premium: PlanTypesEnum.Gold,
};

export class PlanComparisonTableService {
  private highlightedPlanType: PlanType | null;

  private displayedPlanTypes: PlanType[];

  constructor(
    private planGroupQuery: PlanGroup,
    private billingAccountQuery: BillingAccount,
    private paymentPlanQuery: PaymentPlan,
    private t: TranslationGetter,
    private contentStrategy: ContentStrategy,
    private flags: Flags,
    private countryConfig: CountryConfig,
    private suspend?: Suspend | null,
    private addOns?: AddOnsVO | null
  ) {
    // Trial Micro Email users see only the Starter (BRONZE) column in the comparison table.
    this.displayedPlanTypes = this.flags.isMicroEmailTrialer
      ? [PlanTypesEnum.Bronze]
      : this.planGroupQuery.getDisplayedPlanTypes();

    // Use preSuspendPlanId if available (when suspended), otherwise use recommended plan
    const preSuspendPlanId = this.suspend?.getPreSuspendPlanId();
    const preSuspendPlan = preSuspendPlanId ? this.planGroupQuery.getPlanById(preSuspendPlanId) : undefined;

    this.highlightedPlanType = preSuspendPlan?.getType() ?? this.planGroupQuery.getRecommendedPlanType() ?? null;
  }

  public api() {
    return {};
  }

  public data() {
    return {
      getContent: (prepay: string): [ComparisonTablePlan[], ComparisonTableGroup[], ComparisonTableFeature[]] => {
        const discountPercentage = this.paymentPlanQuery.getDiscountPercentage(prepay);

        const plansHeader: ComparisonTablePlan[] = this.contentStrategy
          .getInitialPlans()
          .filter(this.filterUnusedPlansByPlanType)
          .map(this.mapPlansHeader({ discountPercentage }));

        // we assume that the features have the same order as the plans
        const highlightIndex = plansHeader.findIndex((plan) => plan.id === this.highlightedPlanType);

        let newGroupsArray: ComparisonTableGroup[] = this.contentStrategy
          .getInitialGroups()
          .map(this.mapFeatureGroups({ highlightIndex }));

        // Filter out SMS Marketing group if customer is not SMS-eligible
        // The endpoint checks both feature switch AND country eligibility
        if (!this.flags.isSmsEligible) {
          newGroupsArray = newGroupsArray.filter((group) => group.id !== SMS_MARKETING_GROUP_ID);
        }

        const statisTableContent = this.contentStrategy
          .getStaticTableContent()
          .map(this.mapFeatures({ highlightIndex }));

        return [plansHeader, newGroupsArray, statisTableContent];
      },
      getSocialStarterContent: (): {
        plans: ComparisonTablePlan[];
        groups: ComparisonTableGroup[];
      } => {
        const { baseI18n, plans: plansContent, groups: groupsContent } = socialStarterContent;

        const plansFromQuery = this.planGroupQuery.getAllDisplayedPlans();
        const currency = this.billingAccountQuery.getCurrency();

        const perMonthAbbrev = this.t('application-layer.email-plans.per-month-abbrev');

        const plans: ComparisonTablePlan[] = plansFromQuery
          .map((plan) => {
            const planContent = plansContent.find((p) => p.id === plan.getType());

            if (!planContent) {
              return null;
            }

            const price = plan.getPriceForContacts(1)?.format(currency);

            return {
              id: plan.getType(),
              name: this.t(`${baseI18n}.${planContent.nameKey}` as any),
              price: price ?? '-',
              afterPrice: price ? perMonthAbbrev : '',
              variant: plan.getIsRecommendedPlan() ? ('recommended' as const) : undefined,
            };
          })
          .filter((p): p is NonNullable<typeof p> => p !== null);

        const groups: ComparisonTableGroup[] = groupsContent.map((group) => {
          return {
            id: group.id,
            name: this.t(`${baseI18n}.${group.headingKey}` as any),
            features: group.features.map((feature) => ({
              id: feature.id,
              feature: {
                name: this.t(`${baseI18n}.${feature.nameKey}` as any),
                tooltipLabel: '',
              },
              parameters: feature.parameters
                .map((parameter) => {
                  const plan = plansFromQuery.find((p) => p.getType() === parameter.id);

                  if (!plan) {
                    return null;
                  }

                  return {
                    id: parameter.id,
                    value: this.t(`${baseI18n}.${parameter.valueKey}` as any),
                    variant:
                      parameter.id === this.planGroupQuery.getRecommendedPlanType()
                        ? ('recommended' as const)
                        : undefined,
                  };
                })
                .filter((p): p is NonNullable<typeof p> => p !== null),
            })),
          };
        });

        return { plans, groups };
      },
      getSeeMoreFeaturesButtonText: () =>
        this.flags?.isValueFocusedMWebVariant
          ? this.t('application-layer.comparison-table.compare-full-features')
          : this.t('application-layer.comparison-table.see-more-features'),
      getIsMWebVariantEnabled: () => this.flags?.isMWebVariantEnabled,
    };
  }

  public handlers() {
    return {
      onViewAllFeaturesExpand: () => {
        planComparisonTableEventBus.emit('viewAllFeaturesExpanded', { source: 'user' });
      },
      onViewAllFeaturesCollapse: () => {
        planComparisonTableEventBus.emit('viewAllFeaturesCollapsed', { source: 'user' });
      },
      onScrollDepthReached: (featureName: string) => {
        planComparisonTableEventBus.emit('scrollDepthReached', featureName, { source: 'user' });
      },
    };
  }

  // ---------
  // Lifecycle
  // ---------

  public onMount() {
    // No-op
  }

  // ---------
  // Private methods: Callbacks
  // ---------
  private mapPlansHeader =
    ({ discountPercentage }: { discountPercentage: number }) =>
    (plan: InitialPlan) => {
      const firstTier = this.planGroupQuery.getFirstTierForPlan(plan.id);
      const variant = plan.id === this.highlightedPlanType ? ('recommended' as const) : undefined;

      // For Micro Email users, the static content name (e.g. "Lite") is replaced with the
      // backend-sourced plan name. The "Constant Contact - " product prefix is stripped so the
      // column header shows just "Starter", "Lite", "Standard", or "Premium".
      const name = this.flags.isMicroEmail
        ? (this.planGroupQuery.getPlanByType(plan.id)?.getPlanName() ?? plan.name).replace('Constant Contact - ', '')
        : plan.name;

      if (!firstTier) {
        return {
          ...plan,
          name,
          variant,
          price: '',
          prePrice: '',
          afterPrice: '',
          afterPriceMobile: '',
        };
      }

      return {
        ...plan,
        name,
        variant,
        price: firstTier.price.substractPercentage(discountPercentage).format(this.billingAccountQuery.getCurrency()),
      };
    };

  private mapFeatureGroups =
    ({ highlightIndex }: { highlightIndex: number }) =>
    (group: ComparisonTableGroup) => ({
      ...group,
      features: group.features.map(this.mapFeatures({ highlightIndex })),
    });

  private mapFeatures =
    ({ highlightIndex }: { highlightIndex: number }) =>
    (feature: ComparisonTableFeature) => ({
      ...feature,
      parameters: this.resolveSmsPricingPlaceholders(this.resolveUnavilablePlanReferences(feature.parameters))
        .filter(this.filterUnusedPlansByPlanType)
        .map((parameter, index) => ({
          ...parameter,
          variant: index === highlightIndex ? ('recommended' as const) : undefined,
        })),
    });

  // Content strategies render SMS/inbox-preview pricing as placeholder tokens (see ContentStrategy.ts)
  // so the price stays out of static content. Resolve them here from the country-config
  // resolved for this customer (§4a/§4d of the CBU-21106 plan).
  private resolveSmsPricingPlaceholders = (
    parameters: ComparisonTableFeature['parameters']
  ): ComparisonTableFeature['parameters'] =>
    parameters.map((parameter) => {
      if (parameter.value === SMS_STARTING_PRICE_TOKEN) {
        return { ...parameter, value: this.getSmsStartingPriceText() };
      }

      if (parameter.value === SMS_INCLUDED_FREE_TOKEN) {
        return { ...parameter, value: this.getSmsIncludedFreeText() };
      }

      if (parameter.value === INBOX_PREVIEW_PRICE_TOKEN) {
        return { ...parameter, value: this.getInboxPreviewPriceText() };
      }

      return parameter;
    });

  private getSmsStartingPriceText = (): string => {
    const smsStartingPrice = getSmsStartingPriceFromAddOns(this.addOns);

    if (smsStartingPrice == null) {
      return '';
    }

    const price = new PriceVO(smsStartingPrice).formatCompact(this.countryConfig.currency);

    return this.t('application-layer.comparison-table.sms-starting-at', {
      price,
      currency: this.countryConfig.currency,
    });
  };

  private getSmsIncludedFreeText = (): string =>
    this.t('application-layer.comparison-table.sms-included-free', { count: 500 });

  private getInboxPreviewPriceText = (): string => {
    const inboxPreviewStartingPrice = getInboxPreviewStartingPriceFromAddOns(
      this.addOns,
      this.countryConfig.inboxPreviewAddOnId
    );

    if (inboxPreviewStartingPrice == null) {
      return '';
    }

    const price = new PriceVO(inboxPreviewStartingPrice).formatCompact(this.countryConfig.currency);

    return this.t('application-layer.comparison-table.inbox-preview-price', { price });
  };

  // If lite plan is unavilable, standard plan will not show "Same as Lite"
  // but will show the actual value of Lite plan
  private resolveUnavilablePlanReferences = (
    parameters: ComparisonTableFeature['parameters']
  ): ComparisonTableFeature['parameters'] => {
    return parameters.map((param) => {
      if (typeof param.value !== 'string') return param;

      const match = param.value.match(/^Same as (\w+)(.*)/);

      if (!match) return param;

      const [, planName, suffix] = match;
      const referencedPlanType = PLAN_NAME_TO_TYPE[planName];

      if (!referencedPlanType) return param;

      const isReferencedPlanVisible = this.displayedPlanTypes.some((type) => type === referencedPlanType);

      if (isReferencedPlanVisible) return param;

      const referencedParam = parameters.find((p) => p.id === referencedPlanType);

      if (!referencedParam || typeof referencedParam.value !== 'string') return param;

      return { ...param, value: suffix ? `${referencedParam.value}${suffix}` : referencedParam.value };
    });
  };

  // This method filters out the plans that are not displayed in the table
  // The content is static, but the plans are dynamic (based on BE response)
  private filterUnusedPlansByPlanType = ({ id }: { id: string }): boolean => {
    return this.displayedPlanTypes.some((type) => type === id);
  };
}
